import React from 'react';
import { Tabs } from 'antd';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { ChannelConfig } from '../../types/ad-channels';
import { PreviewsMap } from '../../types/demo-previews';
import { Pill } from '@doer/frontend-components/pill';

interface AdChannelTabsProps {
  selectedResolution: string | null;
  onResolutionChange: (resolution: string) => void;
  availableChannels: string[];
  channelConfigs: Record<string, ChannelConfig>;
  getPreviewsForChannel: (channel: string) => PreviewsMap;
}

export const AdChannelTabs: React.FC<AdChannelTabsProps> = ({
  selectedResolution,
  onResolutionChange,
  availableChannels,
  channelConfigs,
  getPreviewsForChannel
}) => {
  const { fontSize, spacing } = useResponsiveSizes();

  const [selectedChannel, setSelectedChannel] = React.useState<string>(availableChannels[0]);

  // Obtener resoluciones por canal
  const resolutionsByChannel = React.useMemo(() => {
    const grouped: Record<string, string[]> = {};

    availableChannels.forEach(channel => {
      const previews = getPreviewsForChannel(channel);
      grouped[channel] = Object.keys(previews);
    });

    return grouped;
  }, [availableChannels, getPreviewsForChannel]);

  // Si el canal seleccionado ya no está disponible, cambiar al primero
  React.useEffect(() => {
    if (!availableChannels.includes(selectedChannel) && availableChannels.length > 0) {
      setSelectedChannel(availableChannels[0]);
    }
  }, [availableChannels, selectedChannel]);

  const tabItems = availableChannels.map(channel => {
    const config = channelConfigs[channel];

    return {
      key: channel,
      label: <span style={{ fontSize: fontSize.xlg, fontWeight: 500 }}>{config.label}</span>,
      children: (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', paddingTop: 8 }}>
          {resolutionsByChannel[channel]?.map(res => {
            const isSelected = selectedResolution === res;
            return (
              <Pill
                key={res}
                size='XS'
                variant={isSelected ? 'outline' : 'ghost'}
                onClick={() => onResolutionChange(res)}
              >
                {res}
              </Pill>
            );
          })}
        </div>
      )
    };
  });

  return (
    <div style={{ width: '100%', marginBottom: spacing.lg }}>
      <Tabs
        activeKey={selectedChannel}
        onChange={key => setSelectedChannel(key)}
        items={tabItems}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      />
    </div>
  );
};
